import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { DsButtonVariants, DsInputStates, DsModalService, DsModalVariant, Icons } from '@levelaccess/design-system';

import { FeatureFlagCollection } from '../../../../../../../shared/interfaces/feature-flag.interface';
import { UserOptinFeatures } from '../../../../../constants/user-optin';
import { FeatureFlagService } from '../../../../../services/feature-flag/feature-flag.service';
import { UserOptinService } from '../../../../../services/user-optin.service';

import { IUserAvatar } from '../../../../../interfaces/user.interface';
import { UserService } from '../../../../../services/user.service';
import { WindowRef } from '../../../../../services/window.ref';
import { CustomValidators } from '../../../../../services/helpers/form-custom-validators';
import { TranslateService } from '../../../../../translate/translate.service';
import { A11yService } from '../../../../../services/a11y.service';
import { FormService } from '../../../../../services/form.service';
import { NotificationService } from '../../../../../services/notification.service';
import { NotificationPosition } from '../../../../../models/notification.model';
import { ErrorMessageService } from '../../../../../services/error-message.service';
import { ITimezone } from '../../../../../../../shared/interfaces/timezone.interface';
import { IMfaStatusResponse, IUserServerResponse } from '../../../../../../../shared/interfaces/user.interface';
import timezones from '../../../../../../../shared/constants/timezones.json';
import { ENGLISH_VARIATION_LOCALE_CODE } from '../../../../../../../shared/constants/translations';
import { RoutingService } from '../../../../../services/routing.service';
import { $mfaStatus, $user } from '../../../../../../../shared/constants/user';
import { alertType } from '../../../../../constants/alert.constants';
import { ITranslationClientConfig } from '../../../../../../../shared/interfaces/translations.interface';
import { SharedCommonUtility } from '../../../../../../../shared/utils/common.utility';
import { MfaRegistrationModalComponent } from './mfa-registration-modal/mfa-registration-modal.component';
import { LoadErrorHandler } from '../../../../../services/load-error-handler';
import { AngularUtility } from '../../../../../utility/angular.utility';
import { IUserRole } from '../../../../../../../shared/interfaces/user-role.interface';
import { $userRole } from '../../../../../../../shared/constants/user-role';
import { UserRoleService } from '../../../../../services/user-role.service';
import { avatarFile } from '../../../../../../../shared/constants/multer';
import { AccountProfileTabsComponent } from './account-profile-tabs.component';
import { BannerType } from '../../../../../components/users/edit-user/edit-permissions-banner/edit-permissions-banner.component';
import { DateUtility } from '../../../../../utility/date.utility';
import { MimeTypeToFileExtension } from '../../../../../../../shared/constants/mime-type';

export enum FormField {
  AVATAR = 'avatar',
  EMAIL = 'email',
  DISPLAY_NAME = 'displayName',
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  USER_ROLE = 'userRole',
  TIMEZONE = 'timezone',
  USER_LANGUAGE = 'userLanguage',
}

@Component({
  selector: 'app-account-profile',
  templateUrl: './account-profile.component.html',
  styleUrls: ['./account-profile.component.scss'],
  standalone: false,
})
export class AccountProfileComponent implements OnInit, OnDestroy {
  public readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public readonly DsInputStates: typeof DsInputStates = DsInputStates;
  public readonly Icons: typeof Icons = Icons;
  public readonly FormField: typeof FormField = FormField;
  public readonly $userRole: typeof $userRole = $userRole;
  public readonly $mfaStatus: typeof $mfaStatus = $mfaStatus;
  public readonly alertType: typeof alertType = alertType;
  public readonly timezones: ITimezone[] = timezones;
  public readonly BannerType: typeof BannerType = BannerType;

  @Input() modal: boolean = false;

  public showVerticalNavigationOptin$: Observable<boolean>;
  public verticalNavigationOptinEnabled$: Observable<boolean>;

  private subscription: Subscription = new Subscription();

  public form: UntypedFormGroup;
  public pageTitle: string = '';
  public user: IUserServerResponse = null;
  public userRoles$: Observable<IUserRole[]>;
  public languages$: Observable<ITranslationClientConfig[]>;
  public isApplicationReloadRequired: boolean = false;
  public mfaStatusResponse$: BehaviorSubject<IMfaStatusResponse> = new BehaviorSubject(null);
  public displayNameError$: Observable<string>;
  public userRoleError$: Observable<string>;
  public updateErrorMessage: string | null;

  @Output() readonly profileSaveSuccess: EventEmitter<IUserServerResponse> = new EventEmitter<IUserServerResponse>();
  @Output() readonly profileSaveError: EventEmitter<HttpErrorResponse> = new EventEmitter<HttpErrorResponse>();

  constructor(
    private windowRef: WindowRef,
    private userService: UserService,
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService,
    private a11yService: A11yService,
    private formService: FormService,
    private notificationService: NotificationService,
    private errorMessageService: ErrorMessageService,
    private routingService: RoutingService,
    private loadErrorHandler: LoadErrorHandler,
    private dsModalService: DsModalService,
    private userRoleService: UserRoleService,
    private featureFlagService: FeatureFlagService,
    private userOptinService: UserOptinService,
  ) {
    this.createForm();

    this.pageTitle = this.routingService.getPageTitle();
    this.userRoles$ = this.userRoleService.getUserRoles();
    this.languages$ = this.getAvailableLanguages();

    this.initializeShowVerticalNavigationOptin();

    this.verticalNavigationOptinEnabled$ = this.userOptinService.hasOptedIn$(UserOptinFeatures.verticalNavigation);
  }

  private initializeShowVerticalNavigationOptin(): void {
    this.showVerticalNavigationOptin$ = combineLatest([
      this.featureFlagService.variation$(FeatureFlagCollection.verticalNavigation, false).pipe(map(Boolean)),
      this.featureFlagService.variation$(FeatureFlagCollection.verticalNavigationOptin, false).pipe(map(Boolean)),
      this.featureFlagService.variation$(FeatureFlagCollection.moveNavToggle, false).pipe(map(Boolean)),
    ]).pipe(
      map(
        ([isVerticalNavigationEnabled, isVerticalNavigationOptinEnabled, isMoveNavToggleEnabled]: [boolean, boolean, boolean]) =>
          !isVerticalNavigationEnabled && isVerticalNavigationOptinEnabled && isMoveNavToggleEnabled,
      ),
      distinctUntilChanged(),
    );
  }

  private createForm(): void {
    this.form = this.formBuilder.group({
      [FormField.AVATAR]: new UntypedFormControl(null),
      [FormField.EMAIL]: new UntypedFormControl(null),
      [FormField.DISPLAY_NAME]: new UntypedFormControl(null, [CustomValidators.validateIsEmpty]),
      [FormField.FIRST_NAME]: new UntypedFormControl(null),
      [FormField.LAST_NAME]: new UntypedFormControl(null),
      [FormField.USER_ROLE]: this.formBuilder.control(null, [Validators.required]),
      [FormField.TIMEZONE]: new UntypedFormControl(null),
      [FormField.USER_LANGUAGE]: new UntypedFormControl(null),
    });

    this.buildDisplayNameErrorObservable(this.form.get(FormField.DISPLAY_NAME));
    this.buildUserRoleErrorObservable(this.form.get(FormField.USER_ROLE));
  }

  private buildDisplayNameErrorObservable(control: AbstractControl): void {
    this.displayNameError$ = control.statusChanges.pipe(
      map(() => this.formService.getErrorMessageForField(control, FormField.DISPLAY_NAME)),
      AngularUtility.shareRef(),
    );
  }

  private buildUserRoleErrorObservable(control: AbstractControl): void {
    this.userRoleError$ = control.statusChanges.pipe(
      map(() => this.formService.getErrorMessageForField(control, FormField.USER_ROLE)),
      AngularUtility.shareRef(),
    );
  }

  private getAvailableLanguages(): Observable<ITranslationClientConfig[]> {
    return this.translateService
      .getListAvailableTranslations()
      .pipe(
        map((languages: ITranslationClientConfig[]) =>
          languages.filter((locale: ITranslationClientConfig) => locale.locale !== ENGLISH_VARIATION_LOCALE_CODE),
        ),
      );
  }

  public onSubmit(): void {
    Object.values(this.form.controls).forEach((control: AbstractControl) => control.updateValueAndValidity());
    if (this.form.valid === false) {
      this.profileSaveError.emit(new HttpErrorResponse({ error: 'Form is not valid' }));
      return;
    }

    this.updateErrorMessage = null;
    this.a11yService.setMessage(this.translateService.instant('saving_your_profile_data'));

    const formData: FormData = this.getFormData(this.form);

    this.subscription.add(
      this.userService.saveProfile(formData).subscribe({
        next: (user: IUserServerResponse) => {
          this.onSaveProfileSuccess(user);
          this.profileSaveSuccess.emit(user);
        },
        error: (response: HttpErrorResponse) => {
          this.onSaveProfileError(response);
          this.profileSaveError.emit(response);
        },
      }),
    );
  }

  private getFormData(form: UntypedFormGroup): FormData {
    const formData: FormData = new FormData();

    const avatarFormControl: AbstractControl = this.form.get(FormField.AVATAR);
    if (SharedCommonUtility.notNullish(avatarFormControl.value?.blob)) {
      const avatarImgExtension: string = MimeTypeToFileExtension[avatarFormControl.value?.blob.type];
      formData.set(avatarFile, avatarFormControl.value.blob, `user_avatar${avatarImgExtension}`);
    }

    formData.set($user.displayName, form.get(FormField.DISPLAY_NAME).value);
    formData.set($user.firstName, form.get(FormField.FIRST_NAME).value);
    formData.set($user.lastName, form.get(FormField.LAST_NAME).value);
    formData.set($user.roleId, form.get(FormField.USER_ROLE).value);
    formData.set($user.timezone, form.get(FormField.TIMEZONE).value);
    formData.set($user.userLanguage, form.get(FormField.USER_LANGUAGE).value);

    return formData;
  }

  private onSaveProfileSuccess(user: IUserServerResponse): void {
    this.isApplicationReloadRequired = this.user[$user.userLanguage] !== user[$user.userLanguage];
    this.user = user;
    this.updateFormData(user);

    this.notificationService.success(this.translateService.instant('success_updating_profile'), NotificationPosition.Toast);
    this.form.markAsPristine();
  }

  private onSaveProfileError(response: HttpErrorResponse): void {
    const errorMessage: string = this.errorMessageService.getAppErrorResponse(response);
    if (typeof errorMessage === 'string') {
      this.updateErrorMessage = this.translateService.instant(errorMessage);
    }

    console.error('onSaveProfileError', response);
  }

  public reloadApplication(): void {
    this.windowRef.windowObject.location.reload();
  }

  public onVerticalNavigationOptinChange(value: boolean): void {
    this.a11yService.setMessage(this.translateService.instant('navigation_switched_announcement'));
    this.userOptinService.updateOptin(UserOptinFeatures.verticalNavigation, value);
  }

  public enableMfa(): void {
    const modalRef = this.dsModalService.open(MfaRegistrationModalComponent, DsModalVariant.center);

    this.subscription.add(
      modalRef.componentInstance.onMfaEnabled.subscribe(() => {
        this.notificationService.success(this.translateService.instant('mfa_verified_successfully'), NotificationPosition.Toast);
        this.updateMfaStatus();
      }),
    );
  }

  public disableMfa(): void {
    this.subscription.add(
      this.userService.disableMfa().subscribe({
        next: () => {
          this.notificationService.success(
            this.translateService.instant('mfa_disabled_successfully'),
            NotificationPosition.Toast,
          );
          this.updateMfaStatus();
        },
        error: (error: HttpErrorResponse) => {
          this.loadErrorHandler.handleError('mfa_failed_to_disable', error, AccountProfileTabsComponent.name);
        },
      }),
    );
  }

  public ngOnInit(): void {
    this.subscription.add(
      this.userService.getAuthenticatedUserProfile().subscribe((user: IUserServerResponse) => {
        this.user = user;
        this.updateFormData(user);
      }),
    );

    this.updateMfaStatus();
  }

  private updateFormData(user: IUserServerResponse): void {
    if (SharedCommonUtility.isNullish(user)) {
      return;
    }

    const avatar: IUserAvatar = {
      src: this.userService.getAvatar(user),
      alt: this.translateService.instant(
        SharedCommonUtility.isNullish(user[$user.avatar]) ? 'default_avatar_alt' : 'edit_profile_user_avatar_alt_text',
      ),
    };

    this.form.get(FormField.AVATAR).setValue(avatar);
    this.form.get(FormField.EMAIL).setValue(user[$user.email]);
    this.form.get(FormField.DISPLAY_NAME).setValue(user[$user.displayName]);
    this.form.get(FormField.FIRST_NAME).setValue(user[$user.firstName]);
    this.form.get(FormField.LAST_NAME).setValue(user[$user.lastName]);
    this.form.get(FormField.USER_ROLE).setValue(user[$user.roleId] ?? null);
    this.form.get(FormField.TIMEZONE).setValue(DateUtility.getTimezoneValueFromUtc(user[$user.timezone] ?? null));

    if (SharedCommonUtility.notNullish(user[$user.userLanguage])) {
      this.form.get(FormField.USER_LANGUAGE).setValue(user[$user.userLanguage].toLowerCase());
    }
  }

  private updateMfaStatus(): void {
    this.subscription.add(
      this.userService.getMfaStatus().subscribe({
        next: (response: IMfaStatusResponse) => this.mfaStatusResponse$.next(response),
        error: (error: HttpErrorResponse) =>
          this.loadErrorHandler.handleError('mfa_failed_to_get_status', error, AccountProfileTabsComponent.name),
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
